// PanelTransom.js
// Transom section for Steel Doors.
// Owns its controlMap; bubbles up the full section data via onChange(sectionData).
// All show/hide dependencies are driven by React state — no DOM manipulation.

import React, { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import Transom           from '../../controls/transom/Transom';
import OverallHeight     from '../../controls/transom/OverallHeight';
import TransomGlass      from '../../controls/transom/TransomGlass';
import TransomLite       from '../../controls/transom/TransomLite';
import LiteCollection    from '../../controls/transom/LiteCollection';
import LiteCollectionData from '../../controls/transom/LiteCollectionData';
import TransomOption     from '../../controls/transom/TransomOption';
import TransomCasing     from '../../controls/transom/TransomCasing';
import CasingSize        from '../../controls/transom/CasingSize';
import TransomOnLoose    from '../../controls/transom/TransomOnLoose';

const ev = { value: '', label: '' };

const PanelTransom = ({
  data         = {},
  onChange,
  selectedDoor = null,
  disabled     = false,
  sectionId    = 'Transom',
  state,
  onExpandChange,
  forceExpanded,
  frameHeight,
}) => {

  // ── section state ─────────────────────────────────────────────────────────
  const [transom,            setTransom]            = useState(data.transom            || ev);
  const [overallHeight,      setOverallHeight]      = useState(data.overallHeight      || ev);
  const [transomGlass,       setTransomGlass]       = useState(data.transomGlass       || ev);
  const [transomLite,        setTransomLite]        = useState(data.transomLite        || ev);
  const [liteCollection,     setLiteCollection]     = useState(data.liteCollection     || ev);
  const [liteCollectionData, setLiteCollectionData] = useState(data.liteCollectionData || ev);
  const [transomOption,      setTransomOption]      = useState(data.transomOption      || ev);
  const [transomCasing,      setTransomCasing]      = useState(data.transomCasing      || ev);
  const [casingSize,         setCasingSize]         = useState(data.casingSize         || ev);
  const [transomOnLoose,     setTransomOnLoose]     = useState(data.transomOnLoose     || ev);

  // ── conditional visibility flags ─────────────────────────────────────────
  // Transom children: shown when transom ≠ None/''
  const [showTransomChildren,    setShowTransomChildren]    = useState(false);
  // LiteCollection + data: shown when TransomLite = 'Other'
  const [showLiteCollection,     setShowLiteCollection]     = useState(false);
  // LiteCollectionData: shown when a standard collection is selected
  const [showLiteCollectionData, setShowLiteCollectionData] = useState(false);
  // TransomOption: shown when LiteCollection = CustomGlassCutoutOptions
  const [showTransomOption,      setShowTransomOption]      = useState(false);
  // CasingSize: shown when TransomCasing is a wood material
  const [showCasingSize,         setShowCasingSize]         = useState(false);

  // ── sync when parent loads saved data (once, when data arrives non-empty) ─
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    if (!data || Object.keys(data).length === 0) return;
    loadedRef.current = true;
    setTransom(data.transom                       || ev);
    setOverallHeight(data.overallHeight           || ev);
    setTransomGlass(data.transomGlass             || ev);
    setTransomLite(data.transomLite               || ev);
    setLiteCollection(data.liteCollection         || ev);
    setLiteCollectionData(data.liteCollectionData || ev);
    setTransomOption(data.transomOption           || ev);
    setTransomCasing(data.transomCasing           || ev);
    setCasingSize(data.casingSize                 || ev);
    setTransomOnLoose(data.transomOnLoose         || ev);
  }, [data]);

  // ── bubble up — gated by isMounted ref so it never triggers its own re-run ─
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    onChange?.({
      transom, overallHeight, transomGlass, transomLite,
      liteCollection, liteCollectionData, transomOption,
      transomCasing, casingSize, transomOnLoose,
    });
  }, [
    transom, overallHeight, transomGlass, transomLite,
    liteCollection, liteCollectionData, transomOption,
    transomCasing, casingSize, transomOnLoose,
  ]);

  // ── section title ─────────────────────────────────────────────────────────
  const title = transom?.value && transom.value !== 'None'
    ? `🪟 Transom  (${transom.label})`
    : '🪟 Transom';

  // ── controlMap ────────────────────────────────────────────────────────────
  const controlMap = [
    {
      id: 'Transom',
      component: (
        <Transom
          value={transom}
          onChange={setTransom}
          disabled={disabled}
          onChildrenVisible={setShowTransomChildren}
        />
      ),
      position: [1, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when Transom ≠ None
    ...(showTransomChildren ? [
      {
        id: 'OverallHeight',
        component: (
          <OverallHeight
            value={overallHeight}
            onChange={setOverallHeight}
            disabled={disabled}
            frameHeight={frameHeight}
          />
        ),
        position: [1, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'TransomGlass',
        component: (
          <TransomGlass
            value={transomGlass}
            onChange={setTransomGlass}
            disabled={disabled}
          />
        ),
        position: [1, 3],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'TransomLite',
        component: (
          <TransomLite
            value={transomLite}
            onChange={setTransomLite}
            disabled={disabled}
            selectedDoor={selectedDoor}
            onLiteCollectionVisible={setShowLiteCollection}
          />
        ),
        position: [2, 1],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'TransomCasing',
        component: (
          <TransomCasing
            value={transomCasing}
            onChange={setTransomCasing}
            disabled={disabled}
            onCasingSizeVisible={setShowCasingSize}
          />
        ),
        position: [2, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'TransomOnLoose',
        component: (
          <TransomOnLoose
            value={transomOnLoose}
            onChange={setTransomOnLoose}
            disabled={disabled}
          />
        ),
        position: [2, 3],
        status: 'active',
        expandableSectionId: sectionId,
      },
    ] : []),
    // conditional: shown when CasingSize material is wood
    ...(showCasingSize ? [{
      id: 'CasingSize',
      component: (
        <CasingSize
          value={casingSize}
          onChange={setCasingSize}
          disabled={disabled}
        />
      ),
      position: [3, 1],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    // conditional: shown when TransomLite = 'Other'
    ...(showLiteCollection ? [{
      id: 'LiteCollection',
      component: (
        <LiteCollection
          value={liteCollection}
          onChange={setLiteCollection}
          disabled={disabled}
          onLiteCollectionDataVisible={setShowLiteCollectionData}
          onTransomOptionVisible={setShowTransomOption}
        />
      ),
      position: [4, 1],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    // conditional: shown when LiteCollection ≠ CustomGlassCutoutOptions
    ...(showLiteCollectionData ? [{
      id: 'LiteCollectionData',
      component: (
        <LiteCollectionData
          value={liteCollectionData}
          onChange={setLiteCollectionData}
          disabled={disabled}
          liteCollection={liteCollection}
        />
      ),
      position: [4, 2],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    // conditional: shown when LiteCollection = CustomGlassCutoutOptions
    ...(showTransomOption ? [{
      id: 'TransomOption',
      component: (
        <TransomOption
          value={transomOption}
          onChange={setTransomOption}
          disabled={disabled}
        />
      ),
      position: [4, 2],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
  ];

  return (
    <ExpandableSection
      title={title}
      sectionId={sectionId}
      state={state}
      onExpandChange={onExpandChange}
      forceExpanded={forceExpanded}
      panelLocation="Transom"
      controlMap={controlMap.filter(c => c.expandableSectionId === sectionId)}
    />
  );
};

export default PanelTransom;
